import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CurrencyFormat from "react-currency-format";
import { getTruckImage, cleanTruckLabel, findVehicle } from "./helpers";
import QuoteSpeedBrag from "./QuoteSpeedBrag";
import QuotePriceSummary from "./QuotePriceSummary";
import { usePricePin } from "./usePricePin";

const bodyLabelOf = (vehicletypes, type) => {
  const v = findVehicle(vehicletypes, type);
  return (v?.bodylabel || "other").replace(/\s*body$/i, "").trim();
};

// Pick the option in this body-type row whose tonnage best matches the
// currently selected vehicle - prefer exact loadgroup match, then closest
// tonnage. Returns the option object (or null) so each row stays anchored to
// the size the customer is comparing.
const findEquivalent = (vehicletypes, options, selectedType) => {
  if (!options.length) return null;
  const target = findVehicle(vehicletypes, selectedType);
  if (!target) return options[0];
  const matchByGroup = options.find((o) => {
    const v = findVehicle(vehicletypes, o.truckType);
    return v?.loadgroup && v.loadgroup === target.loadgroup;
  });
  if (matchByGroup) return matchByGroup;
  const targetT = parseFloat(target.tonnes) || 0;
  let best = options[0];
  let bestDist = Infinity;
  options.forEach((o) => {
    const v = findVehicle(vehicletypes, o.truckType);
    const t = parseFloat(v?.tonnes) || 0;
    const d = Math.abs(t - targetT);
    if (d < bestDist) {
      bestDist = d;
      best = o;
    }
  });
  return best;
};

/**
 * Displays the returned price for the selected vehicle, the trip summary,
 * an "accept" CTA, and the alternative vehicles grouped by body type.
 */
const QuoteResult = ({
  fullQuote,
  truckType,
  loading,
  onAccept,
  onSelectOther,
  vehicletypes,
  optionsStale = false,
  collectiontime = "",
  quoteLatencyMs = null,
  pricePinSlot = null,
  acceptedOffer = false,
  onScrollToBooking,
  // Bumped on every quote action so children (AISuggestions) can mount and
  // fetch in parallel with /quote even before sellingPrice lands.
  aiTrigger = 0,
  // Rendered between the all-in price card and the "Other vehicle options"
  // grid so AI suggestions sit in their own section inside the result.
  children,
}) => {
  const {
    sellingPrice,
    tripDistance,
    tripDuration,
    otherOptions,
    quotedRunners = 0,
    quotedFloors = 0,
    quotedPromocode = "",
    quotedCollectiontime = "",
  } = fullQuote;
  const otherPriced = (otherOptions || []).filter((o) => o.sellingPrice > 0);

  const addOns = [];
  if (quotedRunners > 0) {
    addOns.push({
      icon: "mdi-account-group",
      label: `${quotedRunners} helper${quotedRunners === 1 ? "" : "s"}`,
    });
  }
  if (quotedFloors > 0) {
    addOns.push({
      icon: "mdi-stairs",
      label: `${quotedFloors} floor${quotedFloors === 1 ? "" : "s"}`,
    });
  }
  const displayTime = quotedCollectiontime || collectiontime;
  if (displayTime) {
    addOns.push({
      icon: "mdi-clock-outline",
      label: `Pickup ${displayTime}`,
    });
  }
  if (quotedPromocode) {
    addOns.push({
      icon: "mdi-tag",
      label: `Promo ${quotedPromocode.toUpperCase()}`,
    });
  }

  // Group the alternatives into a row per body type (open / closed / fridge ...).
  const groups = [];
  const groupIndex = {};
  otherPriced.forEach((o) => {
    const body = bodyLabelOf(vehicletypes, o.truckType);
    if (groupIndex[body] === undefined) {
      groupIndex[body] = groups.length;
      groups.push({ body, options: [] });
    }
    groups[groupIndex[body]].options.push(o);
  });

  const pinSourceRef = useRef(null);
  const hasPrice = sellingPrice > 0;
  const { pinned, isDesktop, pinStyle } = usePricePin(
    pinSourceRef,
    hasPrice,
    {
      mobileWatchId: acceptedOffer ? "lp2-makebooking" : null,
    },
  );

  const mobileBarAccept =
    acceptedOffer && onScrollToBooking ? onScrollToBooking : onAccept;
  const mobileBookLabel = acceptedOffer ? "Complete booking" : "Book";

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (pinned && !isDesktop) {
      document.body.classList.add("lp2-has-mobile-price-bar");
    } else {
      document.body.classList.remove("lp2-has-mobile-price-bar");
    }
    return () => document.body.classList.remove("lp2-has-mobile-price-bar");
  }, [pinned, isDesktop]);

  if (!hasPrice && !aiTrigger) return null;

  const priceSummaryProps = {
    truckType,
    sellingPrice,
    tripDistance,
    tripDuration,
    addOns,
    optionsStale,
    loading,
    onAccept,
  };

  return (
    <>
    <div
      className={
        "lp2-result" +
        (loading && hasPrice ? " is-refreshing" : "") +
        (!hasPrice ? " lp2-result--prefetch" : "")
      }
      id={hasPrice ? "lp2-quoteresult" : undefined}
      aria-live={hasPrice ? "polite" : undefined}
      aria-hidden={!hasPrice || undefined}
      aria-busy={hasPrice && loading ? true : undefined}
    >
      {hasPrice && loading && (
        <div className="lp2-result-refresh" aria-live="polite">
          <span className="lp2-spin" aria-hidden="true" />
          Updating price…
        </div>
      )}
      {hasPrice && (
        <div ref={pinSourceRef} className="lp2-result-pin-source">
          <QuotePriceSummary {...priceSummaryProps} variant="inline" showTerms />
        </div>
      )}

      {children && (
        <div className={hasPrice ? "lp2-result-insights" : undefined}>
          {hasPrice && (
            <p className="lp2-result-insights-label" aria-hidden="true">
              Separate from your quoted price
            </p>
          )}
          {children}
        </div>
      )}

      {hasPrice && !loading && <QuoteSpeedBrag latencyMs={quoteLatencyMs} />}

      {hasPrice && groups.length > 0 && (
        <div className="lp2-other">
          <div className="lp2-other-title">Other vehicle options</div>
          {groups.map((group) => (
            <ScrollRow
              key={group.body}
              label={group.body}
              options={group.options}
              selectedType={truckType}
              vehicletypes={vehicletypes}
              onSelect={onSelectOther}
            />
          ))}
        </div>
      )}
    </div>

    {hasPrice &&
      pinned &&
      isDesktop &&
      pricePinSlot &&
      pinStyle &&
      createPortal(
        <QuotePriceSummary
          {...priceSummaryProps}
          variant="pin"
          showTerms={false}
          style={pinStyle}
        />,
        pricePinSlot,
      )}

    {hasPrice &&
      pinned &&
      !isDesktop &&
      typeof document !== "undefined" &&
      createPortal(
        <div className="lp2-portal-root">
          <QuotePriceSummary
            {...priceSummaryProps}
            variant="mobile-bar"
            showTerms={false}
            onAccept={mobileBarAccept}
            bookLabel={mobileBookLabel}
          />
        </div>,
        document.body,
      )}
    </>
  );
};

/**
 * One horizontal scrollable row of vehicle option cards for a single body
 * type. Renders prev/next arrows when the row overflows and snaps to the
 * card whose tonnage matches the currently selected vehicle so the customer
 * is always comparing the same size across rows.
 */
const ScrollRow = ({
  label,
  options,
  selectedType,
  vehicletypes,
  onSelect,
}) => {
  const scrollRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 2;
    setOverflowing(hasOverflow);
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  // Centre the option matching the selected vehicle's tonnage, but ONLY when
  // that target actually changes (or on first mount). Re-renders triggered by
  // arrow-state updates, parent prop refresh, etc. must NOT yank the user's
  // manual scroll back to the matched card.
  const equivalent = findEquivalent(vehicletypes, options, selectedType);
  const equivalentType = equivalent?.truckType || null;
  const lastCenteredRef = useRef(null);
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    // Same target as last time -> don't fight the user's manual scroll.
    if (lastCenteredRef.current === equivalentType) return;
    lastCenteredRef.current = equivalentType;
    if (!equivalentType) return;
    const cards = container.querySelectorAll(".lp2-other-card");
    const idx = options.findIndex((o) => o.truckType === equivalentType);
    const target = cards[idx];
    if (!target) return;
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const desiredLeft =
      container.scrollLeft +
      (tRect.left - cRect.left) -
      (container.clientWidth / 2 - target.clientWidth / 2);
    const clamped = Math.max(
      0,
      Math.min(desiredLeft, container.scrollWidth - container.clientWidth),
    );
    if (Math.abs(clamped - container.scrollLeft) > 4) {
      container.scrollTo({ left: clamped, behavior: "smooth" });
    }
  }, [equivalentType, options]);

  // Recompute arrow state on scroll, resize, and content changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateArrows);
      ro.observe(el);
    }
    window.addEventListener("resize", updateArrows);
    updateArrows();
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  const nudge = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    // Scroll by ~80% of the viewport width so we always reveal new cards
    // without skipping the partially-visible one at the edge.
    const distance = Math.max(140, el.clientWidth * 0.8) * direction;
    el.scrollBy({ left: distance, behavior: "smooth" });
  };

  return (
    <div className="lp2-other-group">
      <div className="lp2-other-grouplabel">{label}</div>
      <div
        className={
          "lp2-other-rowwrap" + (overflowing ? " lp2-other-rowwrap--overflow" : "")
        }
      >
        {overflowing && (
          <button
            type="button"
            className="lp2-other-arrow lp2-other-arrow--prev"
            onClick={() => nudge(-1)}
            disabled={!canPrev}
            aria-label={`Scroll ${label} options left`}
          >
            <i className="mdi mdi-chevron-left" aria-hidden="true" />
          </button>
        )}
        <div className="lp2-other-scroll" ref={scrollRef}>
          {options.map((option) => (
            <button
              type="button"
              key={option.truckType}
              className={
                "lp2-other-card" +
                (selectedType === option.truckType ? " is-selected" : "")
              }
              aria-pressed={selectedType === option.truckType}
              onClick={() => onSelect(option.truckType)}
            >
              <img
                src={getTruckImage(option.truckType)}
                alt={option.truckType}
                loading="lazy"
              />
              <div className="lp2-other-name">
                {cleanTruckLabel(option.truckType)}
              </div>
              <div className="lp2-other-price">
                <CurrencyFormat
                  value={option.sellingPrice / 100}
                  displayType="text"
                  thousandSeparator
                  prefix="R"
                  decimalScale={0}
                />
              </div>
            </button>
          ))}
        </div>
        {overflowing && (
          <button
            type="button"
            className="lp2-other-arrow lp2-other-arrow--next"
            onClick={() => nudge(1)}
            disabled={!canNext}
            aria-label={`Scroll ${label} options right`}
          >
            <i className="mdi mdi-chevron-right" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteResult;
